/*
 * Copyright © 2016-2020 The Thingsboard Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
export default angular.module('agrolog.utils', [])
    .factory('agUtils', AgUtils)
    .name;

/*@ngInject*/
function AgUtils($q, $http, $window, $translate, agTypes, types, deviceService, userService, helpLinks) {

    const gwDefaultLogInterval = 1800;
    const smsDefaultLogInterval = 3600;
    const relayDefaultLogInterval = 3600;
    const modbusDefaultLogInterval = 3600;
    const gnrDefaultLogInterval = 600;

    const defaultSubsysConfig = {
        type: "",
        id: "",
        logging_level: "DEBUG",
        supervisor_config: {
            autostart: true
        },
        controller_config: {
            mqtt_host: "localhost",
            mqtt_user: "",
            mqtt_port: 1883,
            dry_run: false,
            log_interval: null,
            post_register_sleep: 1.0,
            config_mode: false
        },
        hardware_config: {}
    };

    const defaultGatewayHardwareConfig = {
        subsys: "",
        network_config: {
            ip: "",
            netmask: "",
            dns1: "",
            dns2: ""
        }
    };

    const defaultSmsHardwareConfig = {
        subsys: "",
        netcontroller_ids: [],
        serial_port: "",
        timeout: 10
    };

    const defaultRelayHardwareConfig = {
        relays: [],
        serial_port: "",
        subsys: ""
    };

    const defaultModbusHardwareConfig = {
        subsys: "",
        serial_port: "",
        modbus_devices: [],
        timeout: 10
    };

    var service = {
        getAssetPin: getAssetPin,
        getAssetIcon: getAssetIcon,
        createDefaultSubsysConfig: createDefaultSubsysConfig,
        normalizeSubsysConfig: normalizeSubsysConfig,
        getCropNames: getCropNames,
        getCropValues: getCropValues,
        createHelpActionConfig: createHelpActionConfig,
        goToAgHelpPage: goToAgHelpPage,
        getFarmerDeviceName: getFarmerDeviceName,
        ifMoistureDevice: ifMoistureDevice
    };

    return service;

    function getAssetPin(severity, alarmStatus) {
        var pinIcons = agTypes.icons.pin;
        if (pinIcons) {
            if (!severity) {
                return pinIcons.noAlarms;
            } else {
                if (alarmStatus === types.alarmStatus.activeAck) {
                    return pinIcons.hasAckAlarm;
                } else if (severity === 'CRITICAL') {
                    return pinIcons.highest;
                } else {
                    return pinIcons.hasAlarms;
                }
            }
        }
    }

    function getAssetIcon(assetType, severity, alarmStatus) {
        var assetTypeIcons = agTypes.icons[assetType];
        if (assetTypeIcons) {
            if (!severity) {
                return assetTypeIcons.noAlarms;
            } else {
                if (alarmStatus === types.alarmStatus.activeAck) {
                    return assetTypeIcons.hasAckAlarm;
                } else if (severity === 'CRITICAL') {
                    return assetTypeIcons.highest;
                } else {
                    return assetTypeIcons.hasAlarms;
                }
            }
        }
    }

    function goToAgHelpPage($event, helpLinkId, sectionId) {
        if ($event) {
            $event.stopPropagation();
        }
        var helpUrl = helpLinks.linksMap[helpLinkId];
        if (sectionId) {
            helpUrl = helpUrl + "#" + $translate.instant(sectionId);
        }
        $window.open(helpUrl, '_blank');
    }

    function createHelpActionConfig(helpLinkId, sectionId) {
        return {
            name: 'help.goto-help-page',
            show: false,
            onAction: function($event) {
                goToAgHelpPage($event, helpLinkId, sectionId);
            },
            icon: "help"
        };
    }

    function ifMoistureDevice(deviceType) {
        if (deviceType === agTypes.farmerDeviceTypes.farmPro.value ||
            deviceType === agTypes.farmerDeviceTypes.superPro.value ||
            deviceType === agTypes.farmerDeviceTypes.cPro.value ||
            deviceType === agTypes.farmerDeviceTypes.superPoint.value ||
            deviceType === agTypes.farmerDeviceTypes.farmPoint.value) {
            return true;
        }
    }

    function getFarmerDeviceName(deviceType) {
        for (let type in agTypes.farmerDeviceTypes) {
            if (agTypes.farmerDeviceTypes[type].value === deviceType) {
                return agTypes.farmerDeviceTypes[type].name;
            }
        }
    }

    function createDefaultSubsysConfig(device) {
        var deferred = $q.defer();
        deviceService.getDeviceCredentials(device.id.id)
            .then((credentials) => {
                var subsysId = device.name;
                var mqttUser = credentials.credentialsId;
                var subsysConfig = angular.copy(defaultSubsysConfig);
                subsysConfig.id = subsysId;
                subsysConfig.controller_config.mqtt_user = mqttUser;
                if (device.type == agTypes.deviceType.gateway) {
                    subsysConfig.type = "GW";
                    subsysConfig.controller_config.log_interval = gwDefaultLogInterval;
                    subsysConfig.hardware_config = angular.copy(defaultGatewayHardwareConfig);
                } else if (device.type == agTypes.deviceType.sms) {
                    subsysConfig.type = "SMS";
                    subsysConfig.controller_config.log_interval = smsDefaultLogInterval;
                    subsysConfig.hardware_config = angular.copy(defaultSmsHardwareConfig);
                } else if (device.type == agTypes.deviceType.relaySystem) {
                    subsysConfig.type = "RELAY_SYS";
                    subsysConfig.controller_config.log_interval = relayDefaultLogInterval;
                    subsysConfig.hardware_config = angular.copy(defaultRelayHardwareConfig);
                } else if (device.type == agTypes.deviceType.modbus) {
                    subsysConfig.type = "MODBUS";
                    subsysConfig.controller_config.log_interval = modbusDefaultLogInterval;
                    subsysConfig.hardware_config = angular.copy(defaultModbusHardwareConfig);
                } else if (device.type == agTypes.deviceType.generic) {
                    subsysConfig.controller_config.log_interval = gnrDefaultLogInterval;
                }
                subsysConfig.hardware_config.subsys = subsysId;
                deferred.resolve(angular.toJson(subsysConfig));
            },
            () => {
                deferred.reject();
            });
        return deferred.promise;
    }

    function normalizeSubsysConfig(origConfig, deviceType) {
        var normConfig = {
            supervisor_config: {},
            controller_config: {},
            hardware_config: {}
        };
        normConfig.type = origConfig.type;
        normConfig.id = origConfig.id;
        normConfig.logging_level = origConfig.logging_level;
        normConfig.supervisor_config.autostart =
            angular.isDefined(origConfig.supervisor_config) ? (origConfig.supervisor_config.autostart ? true : false) : false;

        var origControllerConfig = angular.isDefined(origConfig.controller_config) ? origConfig.controller_config : {};

        normConfig.controller_config.mqtt_host = origControllerConfig.mqtt_host;
        normConfig.controller_config.mqtt_user = origControllerConfig.mqtt_user;
        normConfig.controller_config.mqtt_port = origControllerConfig.mqtt_port;
        normConfig.controller_config.dry_run = origControllerConfig.dry_run;
        normConfig.controller_config.log_interval = origControllerConfig.log_interval;
        normConfig.controller_config.post_register_sleep = origControllerConfig.post_register_sleep;
        normConfig.controller_config.config_mode = origControllerConfig.config_mode;

        var origHardwareConfig = angular.isDefined(origConfig.hardware_config) ? origConfig.hardware_config : {};
        normConfig.hardware_config.subsys = origHardwareConfig.subsys;
        if (deviceType == agTypes.deviceType.gateway) {
            normConfig.hardware_config.network_config = origHardwareConfig.network_config;
        } else if (deviceType == agTypes.deviceType.sms) {
            normConfig.hardware_config.netcontroller_ids = origHardwareConfig.netcontroller_ids;
            normConfig.hardware_config.serial_port = origHardwareConfig.serial_port;
            normConfig.hardware_config.timeout = origHardwareConfig.timeout;
            normConfig.hardware_config.simulation_config = origHardwareConfig.simulation_config;
        } else if (deviceType == agTypes.deviceType.relaySystem) {
            normConfig.hardware_config.relays = origHardwareConfig.relays;
            normConfig.hardware_config.serial_port = origHardwareConfig.serial_port;
        } else if (deviceType ==  agTypes.deviceType.modbus) {
            normConfig.hardware_config.modbus_devices = origHardwareConfig.modbus_devices;
            normConfig.hardware_config.serial_port = origHardwareConfig.serial_port;
            normConfig.hardware_config.timeout = origHardwareConfig.timeout;
        } else {
            normConfig.hardware_config = origHardwareConfig;
        }
        return normConfig;
    }

    function getCropNames() {
        var deferred = $q.defer();
        var url = '/api/tenant/settings/cropNames';
        $http.get(url).then(
            (response) => {
                deferred.resolve(response.data);
            },
            () => {
                deferred.reject();
            }
        );
        return deferred.promise;
    }

    function getCropValues() {
        var deferred = $q.defer();
        var url = '/api/tenant/settings/cropValues';
        $http.get(url).then(
            (response) => {
                deferred.resolve(response.data);
            },
            () => {
                deferred.reject();
            }
        );
        return deferred.promise;
    }
}
